import { ThemeEvents } from '@theme/events';

const PRODUCT_CARD_GRID_SELECTOR = '.topeak-product-grid, .topeak-shop-all-product-grid';
const PRODUCT_CARD_BUY_SELECTOR = '.topeak-product-grid__buy, .topeak-shop-all-product-grid__buy';
const TOAST_VISIBLE_CLASS = 'is-visible';
const LOADING_CLASS = 'topeak-product-card__buy--loading';
const PENDING_ATTR = 'data-topeak-product-card-add-pending';
const TOAST_DURATION_MS = 4200;
const LOADING_TIMEOUT_MS = 10000;

/** @type {number | undefined} */
let hideTimeoutId;

/** @type {number} */
let hideDeadline = 0;

/** @type {number} */
let remainingMs = TOAST_DURATION_MS;

/**
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function isProductCardForm(form) {
  return (
    form.dataset.type === 'add-to-cart-form' && Boolean(form.closest(PRODUCT_CARD_GRID_SELECTOR))
  );
}

/**
 * @param {HTMLFormElement} form
 * @returns {HTMLButtonElement | null}
 */
function getProductCardBuyButton(form) {
  const button = form.querySelector(PRODUCT_CARD_BUY_SELECTOR);

  return button instanceof HTMLButtonElement ? button : null;
}

/**
 * @param {HTMLElement} productForm
 * @param {boolean} isLoading
 */
function setProductCardBuyLoading(productForm, isLoading) {
  const form = productForm.querySelector('form[data-type="add-to-cart-form"]');

  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  const button = getProductCardBuyButton(form);

  if (!button) {
    return;
  }

  if (isLoading) {
    if (!button.dataset.idleLabel) {
      button.dataset.idleLabel = button.textContent?.trim() || '';
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add(LOADING_CLASS);
    button.textContent = button.dataset.loadingLabel || 'Adding...';
    return;
  }

  button.classList.remove(LOADING_CLASS);
  button.removeAttribute('aria-busy');

  if (button.dataset.idleLabel) {
    button.textContent = button.dataset.idleLabel;
    delete button.dataset.idleLabel;
  }

  button.disabled = false;
}

/**
 * @param {HTMLElement | null} [productForm]
 */
function clearProductCardBuyLoading(productForm = null) {
  const pendingForms = productForm
    ? [productForm].filter((form) => form.hasAttribute(PENDING_ATTR))
    : document.querySelectorAll(`product-form-component[${PENDING_ATTR}]`);

  for (const form of pendingForms) {
    if (!(form instanceof HTMLElement)) {
      continue;
    }

    const timeoutId = Number(form.dataset.productCardAddTimeout || 0);

    if (timeoutId) {
      window.clearTimeout(timeoutId);
      delete form.dataset.productCardAddTimeout;
    }

    form.removeAttribute(PENDING_ATTR);
    setProductCardBuyLoading(form, false);
  }
}

/**
 * @param {HTMLElement} progressBar
 */
function restartProgressBar(progressBar) {
  progressBar.style.animation = 'none';
  progressBar.getBoundingClientRect();
  progressBar.style.removeProperty('animation');
}

/**
 * @param {HTMLElement} toast
 * @param {number} delay
 */
function scheduleHide(toast, delay) {
  if (hideTimeoutId) {
    window.clearTimeout(hideTimeoutId);
  }

  hideDeadline = Date.now() + delay;
  remainingMs = delay;

  hideTimeoutId = window.setTimeout(() => {
    hideToast(toast);
  }, delay);
}

/**
 * @param {HTMLElement} toast
 */
function pauseHide(toast) {
  if (!toast.classList.contains(TOAST_VISIBLE_CLASS)) {
    return;
  }

  if (hideTimeoutId) {
    window.clearTimeout(hideTimeoutId);
    hideTimeoutId = undefined;
    remainingMs = Math.max(0, hideDeadline - Date.now());
  }

  const progressBar = toast.querySelector('[data-topeak-cart-toast-progress]');

  if (progressBar instanceof HTMLElement) {
    progressBar.style.animationPlayState = 'paused';
  }
}

/**
 * @param {HTMLElement} toast
 */
function resumeHide(toast) {
  if (!toast.classList.contains(TOAST_VISIBLE_CLASS)) {
    return;
  }

  const progressBar = toast.querySelector('[data-topeak-cart-toast-progress]');

  if (progressBar instanceof HTMLElement) {
    progressBar.style.animationPlayState = 'running';
  }

  scheduleHide(toast, remainingMs);
}

/**
 * @param {HTMLElement} toast
 */
function hideToast(toast) {
  toast.classList.remove(TOAST_VISIBLE_CLASS);
  toast.hidden = true;

  if (hideTimeoutId) {
    window.clearTimeout(hideTimeoutId);
    hideTimeoutId = undefined;
  }
}

/**
 * @typedef {{ title?: string, showViewCart?: boolean }} ToastVariantOptions
 */

/**
 * @param {HTMLElement} toast
 * @param {'success' | 'error'} variant
 * @param {ToastVariantOptions} [options]
 */
function setToastVariant(toast, variant, options = {}) {
  const isError = variant === 'error';
  const titleElement = toast.querySelector('[data-topeak-cart-toast-title]');
  const successIcon = toast.querySelector('[data-topeak-cart-toast-icon-success]');
  const errorIcon = toast.querySelector('[data-topeak-cart-toast-icon-error]');
  const viewCartLink = toast.querySelector('[data-topeak-cart-toast-view]');

  toast.classList.toggle('topeak-cart-toast--error', isError);
  toast.setAttribute('aria-live', isError ? 'assertive' : 'polite');

  if (titleElement instanceof HTMLElement) {
    titleElement.textContent = isError
      ? options.title?.trim() || toast.dataset.errorTitle || 'Error adding to cart'
      : toast.dataset.successTitle || 'Added to cart';
  }

  if (successIcon instanceof SVGElement) {
    successIcon.hidden = isError;
  }

  if (errorIcon instanceof SVGElement) {
    errorIcon.hidden = !isError;
  }

  if (viewCartLink instanceof HTMLElement) {
    viewCartLink.hidden = isError && !options.showViewCart;
  }
}

/**
 * @param {HTMLElement} toast
 * @param {string} message
 * @param {'success' | 'error'} variant
 * @param {ToastVariantOptions} [options]
 */
function showToast(toast, message, variant = 'success', options = {}) {
  const messageElement = toast.querySelector('[data-topeak-cart-toast-message]');
  const progressBar = toast.querySelector('[data-topeak-cart-toast-progress]');

  setToastVariant(toast, variant, options);

  if (messageElement) {
    messageElement.textContent = message;
  }

  if (progressBar instanceof HTMLElement) {
    restartProgressBar(progressBar);
    progressBar.style.animationPlayState = 'running';
  }

  toast.hidden = false;
  toast.classList.add(TOAST_VISIBLE_CLASS);
  scheduleHide(toast, TOAST_DURATION_MS);
}

/**
 * @param {HTMLElement} toast
 * @param {string} message
 */
function showSuccessToast(toast, message) {
  showToast(toast, message, 'success');
}

/**
 * @param {HTMLElement} toast
 * @param {string} message
 * @param {ToastVariantOptions} [options]
 */
function showErrorToast(toast, message, options = {}) {
  showToast(toast, message, 'error', options);
}

/**
 * @param {Event} event
 * @returns {boolean}
 */
function isProductCardAdd(event) {
  if (!(event.target instanceof Element)) {
    return false;
  }

  const productForm = event.target.closest('product-form-component');

  if (!productForm || !productForm.closest(PRODUCT_CARD_GRID_SELECTOR)) {
    return false;
  }

  const detail = /** @type {{ data?: { didError?: boolean; source?: string } }} */ (event).detail;

  return detail?.data?.source === 'product-form-component' && !detail?.data?.didError;
}

/**
 * @param {Event} event
 * @returns {boolean}
 */
function isSimpleBundleAdd(event) {
  const detail = /** @type {{ data?: { didError?: boolean; source?: string } }} */ (event).detail;

  return detail?.data?.source === 'topeak-simple-bundle' && !detail?.data?.didError;
}

/**
 * @param {Event} event
 * @returns {boolean}
 */
function shouldShowSuccessToast(event) {
  return isProductCardAdd(event) || isSimpleBundleAdd(event);
}

/**
 * @param {HTMLElement} toast
 */
function bindToastInteractions(toast) {
  const closeButton = toast.querySelector('[data-topeak-cart-toast-close]');
  const viewCartLink = toast.querySelector('[data-topeak-cart-toast-view]');

  closeButton?.addEventListener('click', () => {
    hideToast(toast);
  });

  viewCartLink?.addEventListener('click', (event) => {
    event.preventDefault();

    const cartDrawer = document.querySelector('cart-drawer-component');

    if (cartDrawer && 'open' in cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else if (viewCartLink instanceof HTMLAnchorElement) {
      window.location.assign(viewCartLink.href);
    }

    hideToast(toast);
  });

  toast.addEventListener('mouseenter', () => {
    pauseHide(toast);
  });

  toast.addEventListener('mouseleave', () => {
    resumeHide(toast);
  });

  toast.addEventListener('focusin', () => {
    pauseHide(toast);
  });

  toast.addEventListener('focusout', (event) => {
    if (event.relatedTarget instanceof Node && toast.contains(event.relatedTarget)) {
      return;
    }

    resumeHide(toast);
  });
}

function initTopeakCartToast() {
  const toast = document.querySelector('[data-topeak-cart-toast]');

  if (!(toast instanceof HTMLElement)) {
    return;
  }

  const defaultMessage =
    toast.querySelector('[data-topeak-cart-toast-message]')?.textContent?.trim() ||
    'Successfully added to cart';

  bindToastInteractions(toast);

  document.addEventListener('submit', (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !isProductCardForm(form)) {
      return;
    }

    const productForm = form.closest('product-form-component');

    if (!(productForm instanceof HTMLElement)) {
      return;
    }

    productForm.setAttribute(PENDING_ATTR, 'true');
    setProductCardBuyLoading(productForm, true);
    productForm.dataset.productCardAddTimeout = String(
      window.setTimeout(() => {
        if (productForm.hasAttribute(PENDING_ATTR)) {
          showErrorToast(
            toast,
            toast.dataset.errorMessageDefault || 'Could not add to cart'
          );
        }

        clearProductCardBuyLoading(productForm);
      }, LOADING_TIMEOUT_MS)
    );
  });

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    clearProductCardBuyLoading();

    if (!shouldShowSuccessToast(event)) {
      return;
    }

    showSuccessToast(toast, defaultMessage);
  });

  document.addEventListener(ThemeEvents.cartError, (event) => {
    clearProductCardBuyLoading();

    const detail = /** @type {{ data?: { message?: string, title?: string, showViewCart?: boolean } }} */ (
      event
    ).detail;
    const message =
      detail?.data?.message?.trim() ||
      toast.dataset.errorMessageDefault ||
      'Could not add to cart';

    showErrorToast(toast, message, {
      title: detail?.data?.title,
      showViewCart: detail?.data?.showViewCart === true,
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTopeakCartToast, { once: true });
} else {
  initTopeakCartToast();
}
